import React, { useEffect, useState, useMemo } from 'react';
import Button from '../../../../common/components/Button';
import { useLoginMutation } from 'common/api/auth';
import Input from '../../../../common/components/Input';

interface ThinkificConfigFormProps {
  config: {
    lmsUrl: string;
    proviewAdminUsername: string;
    proviewAdminPassword: string;
    proviewName:string;
  };
  setConfig: React.Dispatch<React.SetStateAction<any>>;
  handleUpdateConfiguration: (e: React.FormEvent<HTMLFormElement>) => void;
}

const ThinkificConfigForm: React.FC<ThinkificConfigFormProps> = ({ config, setConfig, handleUpdateConfiguration,}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [login, { data: loginResponse, isLoading, isError }] = useLoginMutation();
  const [urlError, setUrlError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const loginRequest = () => {
    login({
      username: config.proviewAdminUsername,
      password: config.proviewAdminPassword,
    });
  };
  const isValidUrl = (url: string): boolean => {
    try {
      if (url !== url.trim()) return false;
      const urlObj = new URL(url);
      if (/\/+$/.test(url)) return false;
      return /^https?:/.test(urlObj.protocol);
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    if (loginResponse) {
      setIsAuthenticated(true);
      setShowSuccess(true);
    }
  }, [loginResponse]);
  const isFormValid = useMemo(() => {
    return (
      config.proviewName.trim() !== '' &&
      config.proviewAdminUsername.trim() !== '' &&
      config.proviewAdminPassword.trim() !== '' &&
      isValidUrl(config.lmsUrl)
    );
  }, [config]);
  return (
    <form onSubmit={handleUpdateConfiguration}>
      <div className="flex flex-col space-y-6 mx-auto p-4 bg-white">
        <Input
        type="text"
        label="Name :"
        autoComplete="new-name"
        disabled={isAuthenticated}
        value={config.proviewName}
        className='block mb-2'
        onChange={(event) =>
          setConfig((prev) => ({ ...prev, proviewName: event.target.value }))
        }
        required={true}
        />
      <Input
        type="text"
        label="Proview Username :"
        autoComplete="new-username"
        disabled={isAuthenticated}
        value={config.proviewAdminUsername}
        className='block mb-2'
        onChange={(event) =>
          setConfig((prev) => ({ ...prev, proviewAdminUsername: event.target.value }))
        }
        required={true}
      />
      <Input
        type="password"
        label="Password :"
        autoComplete="new-password"
        disabled={isAuthenticated}
        value={config.proviewAdminPassword}
        className='block mb-2'
        onChange={(event) =>
          setConfig((prev) => ({ ...prev, proviewAdminPassword: event.target.value }))
        }
        required={true}
      />
        {isError && (
          <div className="text-sm font-semibold text-red-800 mt-2">
            Incorrect credentials: <br />
            The username or password entered is incorrect, please check and try again.
          </div>
        )}
        <Input
          type="text"
          label="Redirect URL :"
          value={config.lmsUrl}
          disabled={isAuthenticated}
          onChange={(event) => {
            const value = event.target.value;
            setConfig((prev) => ({ ...prev, lmsUrl: value }));
            if (!isValidUrl(value)) {
              setUrlError("Please enter a valid URL without trailing slashes or extra spaces.");
            } else {
              setUrlError(null);
            }
          }}
          required={true}
        />
        {urlError && (
          <div className="text-sm font-semibold text-red-800 mt-2">
            {urlError}
          </div>
        )}
        {showSuccess && (
          <div className="text-sm font-semibold text-green-700 mt-2">
            Credentials verified! Next, click on Update Configuration
          </div>
        )}
        <div className="flex gap-4 mt-4">
      <Button
        label="Verify"
        className="mt-4 bg-emerald-900 hover:bg-emerald-700 text-gray-200 font-bold py-2 px-4 rounded-full inline-flex items-center"
        isLoading={isLoading}
        onClick={loginRequest}
      />
      <Button
        type="submit"
        disabled={!isAuthenticated || !isFormValid}
        className={`mt-4 ${!isAuthenticated || !isFormValid ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-900 hover:bg-emerald-700'} text-gray-200 font-bold py-2 px-4 rounded-full inline-flex items-center`}
        label="Update Configuration"
      />
        </div>
      </div>
    </form>
  );
};

export default ThinkificConfigForm;
